import { useEffect, useState } from "react";
import { ReviewForm } from "./ReviewForm";
import { StarRating } from "./StarRating";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { WarmCard } from "@/components/client/AccountForm";
import { formatDate } from "@/lib/format";
import { reviewStatusLabel } from "@/lib/review-status-label";
import type { ReviewStatus } from "@/types";
import {
  CalendarCheck,
  ChatCircleText,
  CircleNotch,
  PencilSimpleLine,
  Star as StarIcon,
  WarningCircle,
} from "@phosphor-icons/react";

type MyReview = {
  id: string;
  therapistId: string;
  therapistName: string;
  rating: number;
  reviewText: string;
  isAnonymous: boolean;
  status: ReviewStatus;
  rejectionReason: string | null;
  createdAt: string;
};

type ModalState = { therapistId: string; review?: MyReview };

type Reviewable = { therapistId: string; therapistName: string; lastSessionAt: string };

const STATUS_THEME: Record<
  ReviewStatus,
  { bg: string; text: string; ring: string; dot: string; accent: string }
> = {
  pending: {
    bg: "oklch(88% 0.12 80 / 0.16)",
    text: "oklch(45% 0.13 80)",
    ring: "oklch(86% 0.08 80)",
    dot: "oklch(70% 0.14 80)",
    accent: "linear-gradient(180deg, oklch(75% 0.14 80), oklch(66% 0.13 72))",
  },
  approved: {
    bg: "oklch(75% 0.13 150 / 0.13)",
    text: "oklch(40% 0.12 150)",
    ring: "oklch(82% 0.08 150)",
    dot: "oklch(60% 0.12 150)",
    accent: "linear-gradient(180deg, oklch(70% 0.13 150), oklch(60% 0.14 145))",
  },
  rejected: {
    bg: "oklch(70% 0.14 25 / 0.11)",
    text: "oklch(45% 0.13 25)",
    ring: "oklch(86% 0.06 25)",
    dot: "oklch(65% 0.14 25)",
    accent: "linear-gradient(180deg, oklch(70% 0.14 25 / 0.7), oklch(62% 0.13 30 / 0.7))",
  },
  hidden: {
    bg: "oklch(88% 0.01 84 / 0.7)",
    text: "oklch(48% 0.02 84)",
    ring: "oklch(88% 0.015 84)",
    dot: "oklch(64% 0.025 84)",
    accent: "linear-gradient(180deg, oklch(82% 0.015 84), oklch(72% 0.018 84))",
  },
};

export function MyReviewsSection() {
  const [reviews, setReviews] = useState<MyReview[]>([]);
  const [reviewable, setReviewable] = useState<Reviewable[]>([]);
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const [a, b] = await Promise.all([
        fetch("/api/me/reviews").then((r) => r.json()),
        fetch("/api/me/reviewable-therapists").then((r) => r.json()),
      ]);
      setReviews(a.items);
      setReviewable(b.items);
    } catch {
      setError("Recenzije trenutno nije moguće učitati.");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <section className="flex flex-col gap-6">
      <WarmCard>
        <div className="flex flex-col gap-6 p-6 sm:p-8">
          <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div
                className="flex size-11 shrink-0 items-center justify-center rounded-2xl"
                style={{
                  background: "var(--color-brand-bg-light)",
                  border: "1px solid var(--color-brand-bg)",
                }}
              >
                <StarIcon
                  weight="duotone"
                  className="size-5"
                  style={{ color: "var(--color-brand-dark)" }}
                />
              </div>
              <div>
                <p
                  className="text-xs font-semibold uppercase tracking-[0.12em]"
                  style={{ color: "var(--color-brand-muted)" }}
                >
                  Recenzije
                </p>
                <h2 className="font-heading mt-1 text-xl font-semibold tracking-tight text-foreground">
                  Moje recenzije
                </h2>
                <p className="mt-1 max-w-xl text-sm leading-relaxed text-muted-foreground">
                  Nakon završene sesije možete ostaviti ocjenu i kratko iskustvo. Objavljivanje ide
                  kroz administrativni pregled.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:min-w-56">
              <StatPill label="Za slanje" value={reviewable.length} />
              <StatPill label="Poslano" value={reviews.length} />
            </div>
          </header>

          {error ? (
            <div className="flex items-start gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              <WarningCircle weight="fill" className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          {isLoading ? (
            <div className="flex h-48 items-center justify-center">
              <CircleNotch
                className="size-7 animate-spin"
                style={{ color: "var(--color-brand)" }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <section className="flex flex-col gap-3">
                <SectionLabel
                  icon={<PencilSimpleLine weight="duotone" className="size-4" />}
                  title="Možete ostaviti recenziju"
                  count={reviewable.length}
                />

                {reviewable.length > 0 ? (
                  <ul className="flex flex-col gap-3">
                    {reviewable.map((therapist) => (
                      <ReviewableRow
                        key={therapist.therapistId}
                        therapist={therapist}
                        onReview={() => setModalState({ therapistId: therapist.therapistId })}
                      />
                    ))}
                  </ul>
                ) : (
                  <EmptyPanel
                    icon={<CalendarCheck weight="duotone" className="size-6" />}
                    title="Nema novih recenzija za slanje"
                    body="Kada završite sesiju s terapeutom kojeg još niste ocijenili, opcija za recenziju će se pojaviti ovdje."
                  />
                )}
              </section>

              <section className="flex flex-col gap-3">
                <SectionLabel
                  icon={<ChatCircleText weight="duotone" className="size-4" />}
                  title="Vaše recenzije"
                  count={reviews.length}
                />

                {reviews.length > 0 ? (
                  <ul className="flex flex-col gap-3">
                    {reviews.map((review) => (
                      <MyReviewRow
                        key={review.id}
                        review={review}
                        onEdit={() =>
                          setModalState({ therapistId: review.therapistId, review })
                        }
                      />
                    ))}
                  </ul>
                ) : (
                  <EmptyPanel
                    icon={<ChatCircleText weight="duotone" className="size-6" />}
                    title="Još nemate recenzija"
                    body="Vaše poslane recenzije, status pregleda i eventualni razlozi odbijanja biće prikazani ovdje."
                  />
                )}
              </section>
            </div>
          )}
        </div>
      </WarmCard>

      <Dialog open={Boolean(modalState)} onClose={() => setModalState(null)} className="sm:max-w-xl">
        <DialogHeader onClose={() => setModalState(null)}>
          <DialogTitle>{modalState?.review ? "Uredi recenziju" : "Nova recenzija"}</DialogTitle>
          <DialogDescription>
            {modalState?.review
              ? "Ispravite recenziju prema napomeni i pošaljite je ponovo na pregled."
              : "Odaberite ocjenu i napišite kratku recenziju terapeuta."}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {modalState ? (
            <ReviewForm
              therapistId={modalState.therapistId}
              isEdit={Boolean(modalState.review)}
              initialRating={modalState.review?.rating ?? 0}
              initialText={modalState.review?.reviewText ?? ""}
              initialAnonymous={modalState.review?.isAnonymous ?? false}
              onSuccess={() => {
                setModalState(null);
                load();
              }}
            />
          ) : null}
        </DialogBody>
      </Dialog>
    </section>
  );
}

function StatPill({ label, value }: { label: string; value: number }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        background: "oklch(98% 0.005 84)",
        border: "1px solid var(--color-warm-border)",
      }}
    >
      <div
        className="text-2xl font-semibold leading-none"
        style={{ color: "var(--color-brand-dark)", fontFamily: "var(--font-heading)" }}
      >
        {value}
      </div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function SectionLabel({
  icon,
  title,
  count,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h3
        className="flex items-center gap-2 text-sm font-semibold text-foreground"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        <span style={{ color: "var(--color-brand-muted)" }}>{icon}</span>
        {title}
      </h3>
      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
        {count}
      </span>
    </div>
  );
}

function ReviewableRow({
  therapist,
  onReview,
}: {
  therapist: Reviewable;
  onReview: () => void;
}) {
  const initial = therapist.therapistName.trim().charAt(0).toUpperCase();

  return (
    <li
      className="group relative overflow-hidden rounded-2xl bg-white transition-all duration-300 hover:-translate-y-0.5"
      style={{ border: "1px solid var(--color-warm-border)" }}
      onMouseOver={(e) => {
        e.currentTarget.style.boxShadow = "0 8px 24px oklch(37.12% 0.059 186.62 / 0.1)";
        e.currentTarget.style.borderColor = "oklch(88% 0.05 84)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.boxShadow = "";
        e.currentTarget.style.borderColor = "var(--color-warm-border)";
      }}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{
          background:
            "linear-gradient(180deg, oklch(37.12% 0.059 186.62), oklch(29.52% 0.0501 186.62))",
        }}
      />

      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5 sm:pl-6">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className="flex size-11 shrink-0 items-center justify-center rounded-2xl text-sm font-semibold"
            style={{
              background: "var(--color-brand-bg-light)",
              border: "1px solid var(--color-brand-bg)",
              color: "var(--color-brand-dark)",
              fontFamily: "var(--font-heading)",
            }}
          >
            {initial}
          </div>
          <div className="min-w-0">
            <div
              className="truncate text-base font-semibold text-foreground"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {therapist.therapistName}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarCheck weight="duotone" className="size-3.5" />
              <span>Posljednja sesija: {formatDate(therapist.lastSessionAt)}</span>
            </div>
          </div>
        </div>

        <Button onClick={onReview} size="sm" className="w-full rounded-full px-5 sm:w-auto">
          <PencilSimpleLine className="size-4" />
          Ostavi recenziju
        </Button>
      </div>
    </li>
  );
}

function MyReviewRow({ review, onEdit }: { review: MyReview; onEdit: () => void }) {
  const theme = STATUS_THEME[review.status];

  return (
    <li
      className="relative overflow-hidden rounded-2xl bg-white"
      style={{ border: "1px solid var(--color-warm-border)" }}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ background: theme.accent }}
      />
      <div className="flex flex-col gap-3 p-4 sm:p-5 sm:pl-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h4
              className="truncate text-base font-semibold text-foreground"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {review.therapistName}
            </h4>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <StarRating value={review.rating} readOnly size="sm" />
              <span className="text-xs text-muted-foreground">
                {formatDate(review.createdAt)}
              </span>
            </div>
          </div>
          <StatusPill status={review.status} />
        </div>

        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
          {review.reviewText}
        </p>

        {review.status === "rejected" ? (
          <div className="flex flex-col gap-2">
            {review.rejectionReason ? (
              <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs leading-relaxed text-rose-800">
                <WarningCircle weight="fill" className="mt-0.5 size-3.5 shrink-0" />
                <span>
                  <strong>Razlog:</strong> {review.rejectionReason}
                </span>
              </div>
            ) : null}
            <Button
              onClick={onEdit}
              size="sm"
              variant="outline"
              className="w-full rounded-full sm:w-fit"
            >
              <PencilSimpleLine className="size-4" />
              Uredi i pošalji ponovo
            </Button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

function StatusPill({ status }: { status: ReviewStatus }) {
  const theme = STATUS_THEME[status];

  return (
    <span
      className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.08em]"
      style={{ background: theme.bg, color: theme.text, boxShadow: `0 0 0 1px ${theme.ring}` }}
    >
      <span className="inline-block size-1.5 rounded-full" style={{ background: theme.dot }} />
      {reviewStatusLabel[status]}
    </span>
  );
}

function EmptyPanel({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div
      className="rounded-2xl border border-dashed px-5 py-8 text-center"
      style={{
        borderColor: "var(--color-warm-border)",
        background: "oklch(98% 0.005 84 / 0.55)",
      }}
    >
      <div
        className="mx-auto flex size-12 items-center justify-center rounded-2xl"
        style={{
          background: "var(--color-brand-bg-light)",
          border: "1px solid var(--color-brand-bg)",
          color: "var(--color-brand-dark)",
        }}
      >
        {icon}
      </div>
      <h4
        className="mt-4 text-base font-semibold text-foreground"
        style={{ fontFamily: "var(--font-heading)" }}
      >
        {title}
      </h4>
      <p className="mx-auto mt-1.5 max-w-sm text-sm leading-relaxed text-muted-foreground">
        {body}
      </p>
    </div>
  );
}
